import Icon from "../Icon";
import classNamesHelper from "../../helpers/classNameHelper";
import { ChangeEvent, FocusEvent, KeyboardEvent } from "react";
import React, { ReactNode, InputHTMLAttributes, TextareaHTMLAttributes } from "react";
import styles from "./TextInput.module.scss";
import Typography from "../Typography";

export interface ITextInputProps {
  id?: string;
  value?: string;
  placeholder?: string;
  label?: string;
  type?: "text" | "password" | "number" | "datetime-local" | "date";
  size?: "auto";
  onChange?: (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onClear?: () => void;
  onKeyDown?: (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string | null;
  startAdornment?: ReactNode | string;
  endAdornment?: ReactNode | string;
  closeIcon?: boolean;
  pattern?: string;
  maxLength?: number;
  className?: string;
  hintText?: string;
  multiline?: boolean;
  rows?: number;
  inputProps?:InputHTMLAttributes<HTMLInputElement> &
              TextareaHTMLAttributes<HTMLTextAreaElement>;
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

/**
 * Component representing a customizable TextInput.
 * @param value           - Input value
 * @param onChange        - Should be called when the input is changed
 * @param onBlur          - Should be called when the input is blurred
 * @param onClear         - Should be called when the input is emptied
 * @param placeholder     - Placeholder text
 * @param disabled        - Indicate whether the component is being displayed in a disabled state
 * @param label           - Input label
 * @param errorMessage    - Message in an error state
 * @param id              - Input id
 * @param type            - Input type "text" | "password" | "number" | "datetime-local" | "date"
 * @param error           - Indicate whether the component is being displayed in an error state
 * @param closeIcon       - Indicate whether to show the close icon
 * @param pattern         - A regular expression that the input element's value is checked against
 * @param startAdornment  - Used to add a prefix, or an action to an input
 * @param endAdornment    - used to add a a suffix, or an action to an input
 * @param size            - The size of the component
 * @param maxLength       - Max input length
 * @param className       - Additional CSS class
 * @param hintText        - Short hint that describes the expected value of an input field
 * @param multiline       - Render a <textarea> instead of an <input> to allow multi-line entry.
 *                           Defaults to false (single-line <input>).
 * @param rows            - Initial number of visible text rows when `multiline` is true.
 *                          Ignored when `multiline` is false. Defaults to 1.
 * @param inputProps      - Extra props forwarded to the native control (<input> or <textarea>),
 *                          e.g. ARIA attributes (`aria-describedby`), `name`, `autoComplete`,
 *                          `inputMode`, etc. If a key overlaps (e.g. `onChange`), values here
 *                          take precedence and override the component’s own handlers/attrs.
 * @returns TextInput component
 */
export default function TextInput({
  value = "",
  onChange,
  onBlur,
  onClear,
  placeholder = "",
  disabled = false,
  label,
  errorMessage,
  id,
  type = "text",
  error = false,
  closeIcon,
  pattern,
  startAdornment,
  endAdornment,
  size = "auto",
  maxLength,
  className,
  hintText,
  onKeyDown,
  multiline,
  rows,
  inputProps,
  onFocus,
}: ITextInputProps) {
  const hasValue = () => value !== "";

  const validate = (value: any): boolean => {
    if (pattern) {
      const patternRegex = new RegExp(pattern);
      if (!patternRegex.test(value)) {
        return false;
      }
      return true;
    }
    return true;
  };

  const hasStart = startAdornment !== null && startAdornment !== undefined;
  const hasEnd   = endAdornment !== null && endAdornment !== undefined;

  const fieldClass = classNamesHelper([
    styles.input,
    multiline && styles.textarea,
    error && styles.errorInput,
    hasStart && styles.startIconInput,
    hasEnd && styles.endIconInput,
    closeIcon && styles.clearIconInput,
    hasEnd && closeIcon && styles.clearIconWithEndIconInput,
    className,
  ]);

  return (
    <div className={classNamesHelper([styles.inputWrapper, styles[size]])}>
      {label && (
        <Typography size="md" weight="medium" role="label">
          {label}
        </Typography>
      )}
      {
        <div className={styles.inputContainer}>
          {startAdornment && (
            <div className={styles.startAdornment}>{startAdornment}</div>
          )}

          {multiline ? (
          <textarea
            id={id}
            name={id}
            className={fieldClass}
            value={value}
            rows={rows ?? 4}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={onKeyDown as any}
            onBlur={(e) => onBlur && onBlur(e)}
            onChange={(e) =>
              validate(e.target.value)
                ? onChange && onChange(e)
                : e.preventDefault()
            }
            onFocus={onFocus as any}
            {...inputProps}
          />
        ) : (
          <input
            id={id}
            name={id}
            className={fieldClass}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            type={type}
            maxLength={maxLength}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={onKeyDown as any}
            onBlur={(e) => onBlur && onBlur(e)}
            onChange={(e) => (pattern && !new RegExp(pattern).test(e.target.value)) ? e.preventDefault() : onChange?.(e)}
            onFocus={(e) => onFocus?.(e)}               
            {...inputProps}      
          />
        )}

          {endAdornment && (
            <div className={styles.endAdornment}>{endAdornment}</div>
          )}

          {hasValue() && onClear && (
            <label
              role="button"
              htmlFor={id}
              className={classNamesHelper([
                styles.clearIcon,
                hasEnd && styles.clearIconWithEndIcon,
              ])}
              onClick={() => onClear?.()}
            >
              <Icon name="xCircle" size="xs" />
            </label>
          )}
        </div>
      }

      {errorMessage && error && (
        <Typography size="md" className={styles.error} role="error">
          {errorMessage}
        </Typography>
      )}
      {hintText && !error && (
        <Typography size="md" className={styles.hint} role="hint">
          {hintText}
        </Typography>
      )}
    </div>
  );
}
